import type { DataGridFilterConfig, DataGridLeafColumn, DataGridRowClickContext } from '@ibsys/kit';
import {
  CUSTOM_CHART_PIVOT_VALUE_CODE,
  type ChartRuleColumn,
  type CustomChartColor,
  type CustomChartRowConversationIds,
  type CustomChartTableColumn,
  type CustomChartTableGroup,
  type CustomChartTableRow,
} from '@/entities/dashboards';
import { cn } from '@/shared/lib';
import { chartNumberDigits, fmt, slicePaletteBgClassByCode } from '../../utils';
import { chartValueRuleColor, type ChartAppearance } from '../chartElements';
import { chartColumnType, chartDatePrecision, formatChartDate, isChartDateType, shownChartLabel } from '../chartValues';

/** Код колонки группы таблицы с группировкой: тем же именем группа приходит колонкой выборки, в строках её нет. */
export const GROUP_COLUMN_CODE = 'group';

/**
 * Колонки таблицы с группировкой: группа — первой своей колонкой, за ней колонки наполнения.
 * Так таблица с группировкой остаётся одной таблицей — и в показе, и в выборе окрашиваемых колонок.
 *
 * Тип группы называют колонки выборки (`ruleColumns`): по нему группа-дата показывается датой и
 * упорядочивается по ней же. Название колонке группы даёт место показа.
 */
export const groupedTableColumns = (
  columns: CustomChartTableColumn[],
  ruleColumns: ChartRuleColumn[],
  groupTitle: string,
): CustomChartTableColumn[] => [
  { code: GROUP_COLUMN_CODE, title: groupTitle, type: chartColumnType(ruleColumns, GROUP_COLUMN_CODE) },
  ...columns,
];

/** Строки группы таблицы с группировкой: группа проставлена в свою колонку у каждой строки. */
export const groupedTableRows = (group: CustomChartTableGroup): CustomChartTableRow[] =>
  group.rows.map((row) => ({ ...row, [GROUP_COLUMN_CODE]: group.title }));

/** Ширина колонки таблицы: числовая уже текстовой — в ней значение, а не название. */
const TEXT_COLUMN_SIZE = 220;
const NUMBER_COLUMN_SIZE = 140;

/** Ширина колонки с датой: запись `дд.мм.гггг чч:мм:сс` шире числа, но уже названия. */
const DATE_COLUMN_SIZE = 180;

/**
 * Строка таблицы вместе с её порядковым ключом: значения строк могут совпадать целиком. Диалог
 * строки (`null` — его нет) нужен только для перехода и колонкой таблицы не показывается.
 */
export type IndexedTableRow = { key: string; values: CustomChartTableRow; conversationId: number | null };

/** Ширина колонки по типу её значений. */
const columnSize = (type: CustomChartTableColumn['type']): number => {
  if (type === 'number') return NUMBER_COLUMN_SIZE;
  return isChartDateType(type) ? DATE_COLUMN_SIZE : TEXT_COLUMN_SIZE;
};

/** Поле отбора по колонке отвечает виду её значений: текст, число или дата. */
const columnFilter = (type: CustomChartTableColumn['type']): DataGridFilterConfig => {
  if (type === 'number') return { type: 'integer' };
  if (type === 'date') return { type: 'date-range' };
  if (type === 'timestamp') return { type: 'timestamp-range' };
  return { type: 'text' };
};

/** Ячейка строки без диалога в таблице, строки которой ведут к диалогам: нажимаемой она не показана. */
const NOT_CLICKABLE_CELL_CLASS = 'cursor-default';

/**
 * Колонка, по которой окраска выбирается: развёрнутые колонки окрашиваются одной — той колонкой
 * выборки, из которой их развернули, — иначе выбор терялся бы с каждой сменой их состава.
 */
export const tableColorableCode = (column: CustomChartTableColumn): string =>
  column.pivot ? CUSTOM_CHART_PIVOT_VALUE_CODE : column.code;

/**
 * Цвет значения ячейки: числовое красится правилами по величине, текстовое — цветом, заданным
 * ему поимённо. Пока окрашиваемые колонки не выбраны, правила красят все числа таблицы, а цвета
 * значений не действуют: так уже заведённые таблицы выглядят как прежде, а среди всех текстов
 * таблицы окажутся и имена сотрудников, красить которые никто не просил.
 */
const tableCellColor = (
  column: CustomChartTableColumn,
  value: string | number | null | undefined,
  appearance: ChartAppearance | undefined,
): CustomChartColor | undefined => {
  const coloredColumns = appearance?.coloredColumns ?? [];
  const isColumnChosen = coloredColumns.includes(tableColorableCode(column));
  if (coloredColumns.length > 0 && !isColumnChosen) return undefined;

  if (column.type === 'number') {
    return chartValueRuleColor(typeof value === 'number' ? value : null, appearance?.valueColorRules);
  }
  if (!isColumnChosen || value === null || value === undefined) return undefined;
  return appearance?.categoryColors?.[String(value)];
};

/**
 * Строит определения колонок DataGrid по описанию колонок ответа.
 *
 * Значение колонки-даты показывается в виде, принятом для пользователя, а упорядочивается и
 * отбирается по машинной записи: она и остаётся значением ячейки, а вид задаётся только показом.
 * Ряд, по которому берётся точность времени, — сама колонка: значения одной колонки написаны
 * одинаково, а соседняя колонка дат может быть написана иначе. Так же по колонке выбирается и
 * точность чисел: колонка счёта показана целыми, колонка средних — с дробной частью у всех строк.
 *
 * Окраска ограничена выбранными колонками ({@link tableCellColor}): цвет остаётся у самой ячейки,
 * а не у всей строки — правило говорит о величине, а цвет значения — о нём самом.
 *
 * Заголовок колонки — подпись: он показан с заглавной первой буквы ({@link shownChartLabel}), а
 * значения ячеек — такими, какими их дала выборка.
 *
 * `hasRowLinks` — строки таблицы ведут к своим диалогам: строка без диалога тогда показана
 * ненажимаемой.
 */
export const buildTableColumns = (
  columns: CustomChartTableColumn[],
  rows: CustomChartTableRow[],
  appearance: ChartAppearance | undefined,
  hasRowLinks: boolean,
): DataGridLeafColumn<IndexedTableRow>[] =>
  columns.map((column) => {
    const values = rows.map((row) => row[column.code]);
    const precision = chartDatePrecision(values, column.type);
    const digits = chartNumberDigits(values);

    return {
      id: column.code,
      header: shownChartLabel(column.title),
      accessor: (row) => row.values[column.code] ?? null,
      size: columnSize(column.type),
      align: column.type === 'number' ? 'center' : undefined,
      enableSorting: true,
      enableFiltering: true,
      filter: columnFilter(column.type),
      cellClassName: ({ row }) => {
        const color = tableCellColor(column, row.values[column.code], appearance);
        return cn(
          // Окрашенная ячейка залита целиком, без скругления: цвет отмечает ячейку, а не плашку в ней
          color && slicePaletteBgClassByCode(color),
          hasRowLinks && row.conversationId === null && NOT_CLICKABLE_CELL_CLASS,
        );
      },
      cell: ({ value }) => {
        if (column.type === 'number') return fmt(value as number | null, digits);
        if (value === null || value === undefined) return '';
        if (isChartDateType(column.type)) return formatChartDate(String(value), column.type, precision);
        // Группа таблицы с группировкой — подпись, а не значение ячейки: она показана с заглавной,
        // как прочие подписи диаграмм, а значением для раскраски и выбора остаётся как пришла.
        return column.code === GROUP_COLUMN_CODE ? shownChartLabel(String(value)) : String(value);
      },
    };
  });

/**
 * Приписывает строкам ключ по их позиции — одинаковые по значениям строки остаются различимы, — и
 * диалог строки по тому же месту в перечне диалогов ответа.
 */
export const toIndexedRows = (
  rows: CustomChartTableRow[],
  conversationIds: CustomChartRowConversationIds | undefined,
  prefix = '',
): IndexedTableRow[] =>
  rows.map((values, index) => ({ key: `${prefix}${index}`, values, conversationId: conversationIds?.[index] ?? null }));

/**
 * Переход к диалогу по нажатию строки — тот же, что у строки списка диалогов: в текущей вкладке, с
 * Shift — в новой. Ни одна строка диалога не называет или переход погашен — перехода нет вовсе, и
 * строки не показаны нажимаемыми; строка без диалога нажатие пропускает.
 */
export const tableRowConversationOpener = (
  rows: IndexedTableRow[],
  onOpenConversation: ((conversationId: number, openInNewTab: boolean) => void) | undefined,
): ((row: IndexedTableRow, ctx: DataGridRowClickContext) => void) | undefined => {
  if (!onOpenConversation || !rows.some((row) => row.conversationId !== null)) return undefined;
  return (row, ctx) => {
    if (row.conversationId !== null) onOpenConversation(row.conversationId, ctx.event.shiftKey);
  };
};
